import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectChangeEvent,
} from '@mui/material';

export interface PeriodOption {
  value: number;
  label: string;
}

export interface PeriodSelectorProps {
  value: number;
  onChange: (value: number) => void;
  label?: string;
  options?: PeriodOption[];
  fullWidth?: boolean;
  size?: 'small' | 'medium';
}

const defaultOptions: PeriodOption[] = [
  { value: 7, label: '7 Days' },
  { value: 30, label: '30 Days' },
  { value: 60, label: '60 Days' },
  { value: 90, label: '90 Days' },
  { value: 180, label: '180 Days' },
  { value: 365, label: '1 Year' },
];

/**
 * Reusable Period Selector component for time range filters
 *
 * @param value - Currently selected period (in days)
 * @param onChange - Callback function when period changes
 * @param label - Label for the selector (default: "Time Period")
 * @param options - Custom period options (uses default if not provided)
 * @param fullWidth - Whether the select should take full width
 * @param size - Size of the select component
 */
const PeriodSelector: React.FC<PeriodSelectorProps> = ({
  value,
  onChange,
  label = 'Time Period',
  options = defaultOptions,
  fullWidth = false,
  size = 'small',
}) => {
  const handleChange = (event: SelectChangeEvent<number>) => {
    onChange(Number(event.target.value));
  };

  return (
    <FormControl fullWidth={fullWidth} size={size}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={handleChange}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default PeriodSelector;
